import { AutodeskHubModel } from "./lib/types/AutodeskHubModel";
import { AutodeskProjectModel } from "./lib/types/AutodeskProjectModel";
import { AutodeskFolderModel } from "./lib/types/AutodeskFolderModel";
import { AutodeskFolderEntryModel } from "./lib/types/AutodeskFolderEntryModel";
import { AutodeskImportScope } from "./lib/types/AutodeskImportSelectionModel";
import {
  AutodeskImportPreviewModel,
  AutodeskImportRefusalModel,
} from "./lib/types/AutodeskImportPreviewModel";
import { AutodeskImportOutcomeModel } from "./lib/types/AutodeskImportOutcomeModel";

export type AutodeskImportStep =
  | "account"
  | "hub"
  | "project"
  | "folder"
  | "review"
  | "results";

export type AutodeskImportSelection = {
  scope: AutodeskImportScope;
  id: string;
  name: string;
};

export type AutodeskImportState = {
  step: AutodeskImportStep;
  accountId: number | null;
  hub: AutodeskHubModel | null;
  project: AutodeskProjectModel | null;
  /** Empty while showing the project's top folders. */
  folderPath: AutodeskFolderModel[];
  selection: AutodeskImportSelection | null;
  shouldIncludeSubfolders: boolean;
  preview: AutodeskImportPreviewModel | null;
  outcomes: AutodeskImportOutcomeModel[] | null;
  isImporting: boolean;
};

export type AutodeskImportAction =
  | { type: "CHOOSE_ACCOUNT"; accountId: number }
  | { type: "CHOOSE_HUB"; hub: AutodeskHubModel }
  | { type: "CHOOSE_PROJECT"; project: AutodeskProjectModel }
  | { type: "OPEN_FOLDER"; folder: AutodeskFolderModel }
  | { type: "SHOW_HUBS" }
  | { type: "SHOW_PROJECTS" }
  | { type: "SHOW_FOLDER"; depth: number }
  | { type: "SELECT_FILE"; file: AutodeskFolderEntryModel }
  | { type: "SELECT_CURRENT_FOLDER" }
  | { type: "TOGGLE_SUBFOLDERS" }
  | {
      type: "PREVIEW_LOADED";
      selection: AutodeskImportSelection;
      preview: AutodeskImportPreviewModel;
    }
  | { type: "IMPORT_STARTED" }
  | { type: "IMPORT_STOPPED" }
  | {
      type: "IMPORT_FINISHED_WITH_FAILURES";
      selection: AutodeskImportSelection;
      outcomes: AutodeskImportOutcomeModel[];
    }
  | {
      type: "IMPORT_REFUSED";
      selection: AutodeskImportSelection;
      refusal: AutodeskImportRefusalModel;
    }
  | { type: "RESET" };

export const initialAutodeskImportState: AutodeskImportState = {
  step: "account",
  accountId: null,
  hub: null,
  project: null,
  folderPath: [],
  selection: null,
  shouldIncludeSubfolders: false,
  preview: null,
  outcomes: null,
  isImporting: false,
};

const noSelection = {
  selection: null,
  preview: null,
  outcomes: null,
} satisfies Partial<AutodeskImportState>;

export function autodeskImportReducer(
  state: AutodeskImportState,
  action: AutodeskImportAction,
): AutodeskImportState {
  if (isForEarlierSelection(state, action)) return state;

  switch (action.type) {
    case "CHOOSE_ACCOUNT":
      return {
        ...initialAutodeskImportState,
        accountId: action.accountId,
        step: "hub",
      };
    case "CHOOSE_HUB":
      return {
        ...state,
        ...noSelection,
        hub: action.hub,
        project: null,
        folderPath: [],
        step: "project",
      };
    case "CHOOSE_PROJECT":
      return {
        ...state,
        ...noSelection,
        project: action.project,
        folderPath: [],
        step: "folder",
      };
    case "OPEN_FOLDER":
      return {
        ...state,
        ...noSelection,
        folderPath: [...state.folderPath, action.folder],
        step: "folder",
      };
    case "SHOW_HUBS":
      return {
        ...state,
        ...noSelection,
        hub: null,
        project: null,
        folderPath: [],
        step: "hub",
      };
    case "SHOW_PROJECTS":
      return {
        ...state,
        ...noSelection,
        project: null,
        folderPath: [],
        step: "project",
      };
    case "SHOW_FOLDER":
      return {
        ...state,
        ...noSelection,
        folderPath: state.folderPath.slice(0, action.depth),
        step: "folder",
      };
    case "SELECT_FILE":
      return {
        ...state,
        ...noSelection,
        selection: {
          scope: "Item",
          id: action.file.id,
          name: action.file.name,
        },
        step: "review",
      };
    case "SELECT_CURRENT_FOLDER": {
      const folder = state.folderPath.at(-1);
      if (!folder) return state;
      return {
        ...state,
        ...noSelection,
        selection: {
          scope: state.shouldIncludeSubfolders ? "FolderTree" : "Folder",
          id: folder.id,
          name: folder.name,
        },
        step: "review",
      };
    }
    case "TOGGLE_SUBFOLDERS":
      return {
        ...state,
        shouldIncludeSubfolders: !state.shouldIncludeSubfolders,
      };
    case "PREVIEW_LOADED":
      return { ...state, preview: action.preview };
    case "IMPORT_STARTED":
      return { ...state, isImporting: true };
    case "IMPORT_STOPPED":
      return { ...state, isImporting: false };
    case "IMPORT_FINISHED_WITH_FAILURES":
      return { ...state, outcomes: action.outcomes, step: "results" };
    case "IMPORT_REFUSED":
      return {
        ...state,
        preview: state.preview && { ...state.preview, refusal: action.refusal },
        step: "review",
      };
    case "RESET":
      return initialAutodeskImportState;
  }
}

/** A response can arrive after the user has moved on to another selection. */
function isForEarlierSelection(
  state: AutodeskImportState,
  action: AutodeskImportAction,
) {
  return "selection" in action && action.selection !== state.selection;
}
